import {
  useGetCoreActiveLocationsCountQuery,
  useGetCoreActiveProductsCountQuery,
} from '@autone/ui';
import { type Control, useWatch } from 'react-hook-form';

import { type DownloadAssortmentTemplateFormSchema } from '../features/assortment-template-modals/download-template-modal/DownloadTemplateModal.schema';

export const useWatchLocationScope = (
  control: Control<DownloadAssortmentTemplateFormSchema>,
) => useWatch({ control, name: 'filters.geographic' });

export const useWatchProductScope = (
  control: Control<DownloadAssortmentTemplateFormSchema>,
) => useWatch({ control, name: 'filters.product' });

export const useWatchLocationClustersScope = (
  control: Control<DownloadAssortmentTemplateFormSchema>,
) => useWatch({ control, name: 'filters.locationClusters' });

export const useGetLocationsCount = (
  control: Control<DownloadAssortmentTemplateFormSchema>,
) => {
  const locationClusters = useWatchLocationClustersScope(control);
  const { locationTypes, regions, locations, countries } =
    useWatchLocationScope(control) || {};

  return useGetCoreActiveLocationsCountQuery(
    {
      body: {
        filters: {
          geographic: {
            location_types: locationTypes,
            regions: regions,
            countries: countries,
            locations: locations,
          },
          location_groups: {},
          location_cluster: {
            location_clusters: locationClusters,
          },
        },
      },
    },
    {
      selectFromResult: ({ data, ...state }) => ({
        count: data?.count ?? 0,
        ...state,
      }),
    },
  );
};

export const useGetProductsCount = (
  control: Control<DownloadAssortmentTemplateFormSchema>,
) => {
  const locationClusters = useWatchLocationClustersScope(control);
  const {
    departments,
    subDepartments,
    collectionTypes,
    seasons,
    events,
    brands,
    productGroups,
    productIds,
    manufacturers,
    classes,
    subClasses,
  } = useWatchProductScope(control) || {};

  return useGetCoreActiveProductsCountQuery(
    {
      body: {
        filters: {
          catalogue: {},
          product: {
            departments: departments,
            sub_departments: subDepartments,
            collection_types: collectionTypes,
            seasons: seasons,
            events: events,
            brands: brands,
            manufacturers: manufacturers,
            classes: classes,
            sub_classes: subClasses,
          },
          product_groups: {
            groups: productGroups,
          },
          location_cluster: {
            location_clusters: locationClusters,
          },
          size_run: {},
          size: {},
        },
      },
    },
    {
      selectFromResult: ({ data, ...state }) => ({
        count: productIds ? productIds.length : (data?.count ?? 0),
        ...state,
      }),
    },
  );
};
